/**
 * RightsPanel.tsx — 1804va
 * Creator sets rights at upload time
 */
import { useState } from 'react'
import { Toggle, Card } from '@/components/ui/index'
import { cn } from '@/components/ui/utils'

export interface RightsConfig {
  usage_type: 'exclusive' | 'non-exclusive' | 'editorial' | 'free'
  credit_required: boolean
  embargo_until: string | null
  allowed_audiences: string[]
}

const USAGE_OPTIONS = [
  { value: 'free',           label: 'Free',          desc: 'Anyone can use, no payment' },
  { value: 'editorial',      label: 'Editorial',     desc: 'News/editorial use only' },
  { value: 'non-exclusive',  label: 'Non-exclusive', desc: 'Multiple buyers, Phase 2' },
  { value: 'exclusive',      label: 'Exclusive',     desc: 'One buyer only, Phase 2' },
] as const

const AUDIENCES = ['creators', 'organisers', 'sponsors', 'publishers', 'press'] as const

interface Props {
  value: RightsConfig
  onChange: (v: RightsConfig) => void
}

export function RightsPanel({ value, onChange }: Props) {
  const [showEmbargo, setShowEmbargo] = useState(!!value.embargo_until)

  const update = (patch: Partial<RightsConfig>) => onChange({ ...value, ...patch })

  const toggleAudience = (a: string) => {
    const next = value.allowed_audiences.includes(a)
      ? value.allowed_audiences.filter(x => x !== a)
      : [...value.allowed_audiences, a]
    update({ allowed_audiences: next })
  }

  return (
    <Card className="p-4 space-y-5 bg-neutral-900 border-neutral-800">
      <div>
        <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-3">Usage type</p>
        <div className="grid grid-cols-2 gap-2">
          {USAGE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => update({ usage_type: opt.value })}
              className={cn(
                'text-left px-3 py-2.5 rounded-lg border text-sm transition-all',
                value.usage_type === opt.value
                  ? 'border-pink-500 bg-pink-500/10 text-white'
                  : 'border-neutral-700 text-neutral-400 hover:border-neutral-500'
              )}
            >
              <span className="block font-medium">{opt.label}</span>
              <span className="block text-xs opacity-60 mt-0.5">{opt.desc}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-white">Credit required</p>
          <p className="text-xs text-neutral-500">Recipients must credit you when using this content</p>
        </div>
        <Toggle
          checked={value.credit_required}
          onChange={v => update({ credit_required: v })}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-sm text-white">Embargo</p>
            <p className="text-xs text-neutral-500">Hold content until a specific date/time</p>
          </div>
          <Toggle
            checked={showEmbargo}
            onChange={v => {
              setShowEmbargo(v)
              if (!v) update({ embargo_until: null })
            }}
          />
        </div>
        {showEmbargo && (
          <input
            type="datetime-local"
            value={value.embargo_until?.slice(0,16) ?? ''}
            onChange={e => update({ embargo_until: e.target.value ? new Date(e.target.value).toISOString() : null })}
            className="w-full mt-2 px-3 py-2 rounded-lg bg-neutral-800 border border-neutral-700 text-white text-sm focus:outline-none focus:border-pink-500"
          />
        )}
      </div>

      <div>
        <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-3">Who can receive this</p>
        <div className="flex flex-wrap gap-2">
          {AUDIENCES.map(a => (
            <button
              key={a}
              type="button"
              onClick={() => toggleAudience(a)}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-medium border transition-all capitalize',
                value.allowed_audiences.includes(a)
                  ? 'border-pink-500 bg-pink-500/15 text-pink-300'
                  : 'border-neutral-700 text-neutral-500 hover:border-neutral-500'
              )}
            >
              {a}
            </button>
          ))}
        </div>
      </div>
    </Card>
  )
}

export const DEFAULT_RIGHTS: RightsConfig = {
  usage_type: 'free',
  credit_required: true,
  embargo_until: null,
  allowed_audiences: ['creators', 'organisers', 'sponsors', 'publishers', 'press'],
}
